/**
 * Which report is on screen — so a failed save can say WHICH report it was
 * (OFFICEHUB-CLIENT-63).
 *
 * The network interceptor (lib/datahub/errorLogging/consoleAndNetworkCapture)
 * reports a failed `forms_*` callable from a fetch wrapper that sees only the
 * URL, the status and the method. It cannot see the request body, and the page
 * route carries the TEMPLATE id (`/forms/fill/:templateId`), not the
 * submission. Measured 2026-10-08: 30 failed saves in 30 days, and 28 of the 30
 * recorded nothing but the route, so "did this report later reach the server?"
 * could only be answered by guessing on person and time.
 *
 * FormFiller registers a getter here while it is mounted; the interceptor reads
 * it when a forms callable fails and adds the ids as Sentry TAGS.
 *
 * IDS ONLY. Never the form answers, never a customer name: a tag is indexed,
 * searchable and retained, and the ids are exactly what an operator needs to
 * join a failure to the document in Firestore.
 */

import { SERVICE_ID_PATTERN } from '../serviceIdPattern';

export interface ActiveFormIds {
  /** The submission doc id this fill session writes to (client-minted or resumed). */
  readonly submissionId?: string | null;
  readonly templateId?: string | null;
  /** The service ticket the report was launched from, when there is one. */
  readonly serviceTicketId?: string | null;
}

export type ActiveFormIdsGetter = () => ActiveFormIds;

let activeGetter: ActiveFormIdsGetter | null = null;

/**
 * Register the on-screen form. Returns an unregister function that clears the
 * registration ONLY if it is still this one — so a slow unmount of form A can
 * never wipe the registration that form B made a moment earlier.
 */
export function registerActiveForm(getter: ActiveFormIdsGetter): () => void {
  activeGetter = getter;
  return () => {
    if (activeGetter !== getter) return;
    activeGetter = null;
    try {
      recent = { ids: getter(), at: Date.now() };
    } catch {
      recent = null;
    }
  };
}

/** Test-only: restore pristine state so test files cannot couple via order. */
export function __resetActiveFormForTests(): void {
  activeGetter = null;
  recent = null;
}

/**
 * The forms callables whose failure is a failed save of a report — OUR
 * functions host only, anchored at the start of the URL, so a third-party URL
 * that merely contains `/forms_x` in its path or query is never tagged.
 */
const FORMS_CALLABLE =
  /^https?:\/\/[a-z0-9-]+-quoter-saas\.cloudfunctions\.net\/forms_[A-Za-z0-9_]+(?:[?#/]|$)/i;

/** Ids are short opaque tokens; anything else is not an id and is dropped. */
const SAFE_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * How long after a form closes its ids still name a failed forms save. A save
 * in flight when the tech submits and navigates away fails AFTER the page is
 * gone; without this window that failure would report route-only again.
 * Short on purpose: past it the ids could belong to a different report.
 */
const RECENT_WINDOW_MS = 60_000;
let recent: { readonly ids: ActiveFormIds; readonly at: number } | null = null;

function safeId(value: string | null | undefined): string | undefined {
  return typeof value === 'string' && SAFE_ID.test(value) ? value : undefined;
}

/**
 * Sentry tags naming the report a failed forms callable belonged to. Empty for
 * any other URL (a failed billing call is not about the open form) and when no
 * form is registered. Never throws: this runs inside error reporting, where a
 * throw would hide the very failure being reported.
 */
export function activeFormTagsForUrl(url: string): Record<string, string> {
  if (!FORMS_CALLABLE.test(url)) return {};
  try {
    let closedRecently = false;
    let ids: ActiveFormIds | null = null;
    if (activeGetter) {
      ids = activeGetter();
    } else if (recent && Date.now() - recent.at <= RECENT_WINDOW_MS) {
      ids = recent.ids;
      closedRecently = true;
    }
    if (!ids) return {};
    const tags: Record<string, string> = {};
    // 'open' vs 'closed_within_60s' separates the two attribution confidence
    // levels. NAMED LIMIT: a save of report A failing after report B opened is
    // tagged with B's ids (the reporter cannot see the request body).
    tags.form_context = closedRecently ? 'closed_within_60s' : 'open';
    const submissionId = safeId(ids.submissionId);
    const templateId = safeId(ids.templateId);
    // A ticket tag must have the app's service-id shape: the value comes from the
    // URL (`?serviceTicketId=`), so a bare surname or phone number must not pass.
    const serviceTicketId = SERVICE_ID_PATTERN.test(ids.serviceTicketId ?? '') ? safeId(ids.serviceTicketId) : undefined;
    if (submissionId) tags.form_submission_id = submissionId;
    if (templateId) tags.form_template_id = templateId;
    if (serviceTicketId) tags.form_service_ticket_id = serviceTicketId;
    return tags;
  } catch {
    return {};
  }
}
